import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { NotificationService } from '@components/notification/notification.service';
import { saveAs } from 'file-saver';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-complex-file-uploader',
  templateUrl: './complex-file-uploader.component.html',
  styleUrls: ['./complex-file-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComplexFileUploaderComponent {
  @Input() multipleSelection: boolean;
  @Input() nameProp: string;
  @Input() additionalProp: { prop: string; label: string };
  @Input() service: any;
  @Input() upload: { method: any; args: any[] };
  @Input() download: { method: any; args: any[] };
  @Input() delete: { method: any; args: any[] };

  uploadBoxVisible = true;
  isSaving = false;
  filesToUpload: File[] = [];
  records: any[] = [];
  private getListProps: { method: any; args: any[] };

  constructor(
    private readonly notificationService: NotificationService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  @Input()
  set getList(propsData: { method: any; args: any[] }) {
    this.getListProps = propsData;
    this.getRecords();
  }

  onUploadFile({ target }: any): void {
    const inputFiles = [...target.files];
    if (this.checkFiles(inputFiles)) {
      this.filesToUpload = [...this.filesToUpload, ...inputFiles];
      this.handleFileInputChange();
    }
    target.value = '';
  }

  uploadAttachment(): void {
    this.isSaving = true;
    const [file] = this.filesToUpload;
    const formData = new FormData();
    formData.append('file', file, file.name);

    this.upload.method
      .call(this.service, ...this.upload.args, formData)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(() => {
        this.notificationService.showSaved();
        this.getRecords();
        this.uploadBoxVisible = true;
        this.filesToUpload = [];
      });
  }

  downloadFile(id: string, name: string): void {
    this.isSaving = true;
    this.download.method
      .call(this.service, ...this.download.args, id)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe((res: Blob) => {
        saveAs(new File([res], name));
      });
  }

  deleteFileToUpload(index: number): void {
    this.filesToUpload = this.filesToUpload.filter((el, idx) => idx !== index);
    this.handleFileInputChange();
  }

  trackByFn(index: number, { a = {} }: Record<string, unknown>): number {
    return index;
  }

  trackList({ a = {} }: Record<string, unknown>, record: Record<string, string>): string {
    return record.id;
  }

  deleteFile(id: string): void {
    this.isSaving = true;
    this.delete.method
      .call(this.service, ...this.delete.args, id)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(() => {
        this.notificationService.showDeleted();
        this.getRecords();
      });
  }

  private getRecords(): void {
    this.getListProps.method.call(this.service, ...this.getListProps.args).subscribe(({ data }: any) => {
      this.records = data;
      this.cd.detectChanges();
    });
  }

  private checkFiles(files: File[]): boolean {
    return files.length && !this.containsDuplicates(files) && this.checkFilesSize(files);
  }

  private checkFilesSize(files: File[]): boolean {
    const fileSizeState = Boolean(files.filter((file) => file.size > 0).length);

    if (!fileSizeState) {
      this.notificationService.showError({ message: $localize`Plik nie może być pusty` });
    }
    return fileSizeState;
  }

  private containsDuplicates(files: File[]): boolean {
    const hasDuplicates = new Set(files.map((f) => f.name)).size !== files.length;

    if (hasDuplicates) {
      this.notificationService.showError({ message: $localize`Plik o takiej samej nazwie już istnieje` });
    }
    return hasDuplicates;
  }

  private handleFileInputChange(): void {
    if (!this.multipleSelection) {
      this.uploadBoxVisible = !this.filesToUpload.length;
    }
  }
}
